/**
 * =============================================================================
 * WlBillingAgingDetailModal
 *
 * Modal de drill-down do aging da aba Cobrança (WL Financeiro).
 *
 * Abre a partir do click em uma faixa (bucket) do card `WlBillingAgingCard` e
 * lista de forma paginada e pesquisável todas as parcelas em aberto do tenant
 * master classificadas naquele bucket, com o tenant / cliente / cobrança de
 * origem. Reaproveita a RPC `wl_get_billing_kpis` (drill-down opcional).
 *
 * @version 1.0.0
 * RELEASE: 2026-07-22 v1.0.0 - Versão inicial (drill-down do aging).
 * =============================================================================
 */
import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useWlBillingAgingDetails,
  type WlBillingAgingBucket,
} from '@/hooks/wl/useWlBillingKpis';
import { WlTenantDetailsModal } from '@/components/wl/WlTenantDetailsModal';

interface Props {
  open: boolean;
  onClose: () => void;
  bucket: WlBillingAgingBucket | null;
  label: string | null;
}

// Formatador BRL (pt-BR)
const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(v || 0));

// Formatador data pt-BR
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR');
};

// Descrição da regra de negócio de cada bucket
const BUCKET_DESC: Record<WlBillingAgingBucket, string> = {
  future:
    'Parcelas em aberto (pendente / aguardando / vencido) cuja data de vencimento é hoje ou futura.',
  '1_30':
    'Parcelas em aberto vencidas entre 1 e 30 dias em relação a hoje.',
  '31_60':
    'Parcelas em aberto vencidas entre 31 e 60 dias em relação a hoje.',
  '61_90':
    'Parcelas em aberto vencidas entre 61 e 90 dias em relação a hoje.',
  '91_120':
    'Parcelas em aberto vencidas entre 91 e 120 dias em relação a hoje.',
  '120_plus':
    'Parcelas em aberto vencidas há mais de 120 dias em relação a hoje.',
};

// Classe do badge por status (visual leve)
const STATUS_BADGE: Record<string, string> = {
  vencido: 'bg-red-100 text-red-800 border-red-200',
  pendente: 'bg-amber-100 text-amber-800 border-amber-200',
  aguardando_pagamento: 'bg-blue-100 text-blue-800 border-blue-200',
  aguardando_emissao: 'bg-slate-100 text-slate-700 border-slate-200',
};

export function WlBillingAgingDetailModal({ open, onClose, bucket, label }: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [search, setSearch] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);

  // Reset ao trocar bucket ou reabrir
  useEffect(() => {
    if (open && bucket) {
      setPage(1);
      setSearch('');
    }
  }, [open, bucket]);

  const { data, isLoading, error } = useWlBillingAgingDetails(
    open ? bucket : null,
    page,
    pageSize,
    search,
  );

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  // Soma da página (rodapé informativo)
  const pageSum = useMemo(
    () => rows.reduce((acc, r) => acc + Number(r.valor || 0), 0),
    [rows],
  );

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Parcelas — {label ?? 'Faturas pendentes'}
            </DialogTitle>
            <DialogDescription>
              {bucket ? BUCKET_DESC[bucket] : ''}
            </DialogDescription>
          </DialogHeader>

          {/* Busca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por tenant, cliente ou Nº da fatura..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="pl-9"
            />
          </div>

          {/* Tabela */}
          <div className="flex-1 overflow-auto rounded-md border">
            <Table>
              <TableHeader className="sticky top-0 bg-muted/50 z-10">
                <TableRow>
                  <TableHead className="text-xs">Tenant / Cliente</TableHead>
                  <TableHead className="text-xs">Cobrança</TableHead>
                  <TableHead className="text-xs text-center">Parcela</TableHead>
                  <TableHead className="text-xs">Vencimento</TableHead>
                  <TableHead className="text-xs text-center">Dias</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {error && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-destructive py-6">
                      Erro ao carregar: {(error as Error).message}
                    </TableCell>
                  </TableRow>
                )}
                {isLoading && !error && (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={7}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {!isLoading && !error && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                      Nenhuma parcela encontrada neste bucket.
                    </TableCell>
                  </TableRow>
                )}
                {!isLoading && !error && rows.map((r) => {
                  const tenantDisplay =
                    r.customer_name ??
                    r.tenant_name ??
                    r.tenant_slug ??
                    '—';
                  const statusCls = STATUS_BADGE[r.status] ?? 'bg-muted text-foreground border-muted';
                  return (
                    <TableRow key={r.parcela_id}>
                      <TableCell className="py-2">
                        {r.tenant_id ? (
                          <button
                            type="button"
                            onClick={() => setSelectedTenantId(r.tenant_id)}
                            className="text-left text-sm font-medium text-primary hover:underline"
                          >
                            {tenantDisplay}
                          </button>
                        ) : (
                          <span className="text-sm">{tenantDisplay}</span>
                        )}
                        {r.tenant_slug && r.tenant_slug !== tenantDisplay && (
                          <div className="text-[10px] text-muted-foreground uppercase mt-0.5">
                            {r.tenant_slug}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-2 text-[11px] font-mono text-muted-foreground">
                        {r.numero_fatura ?? r.cobranca_id.slice(0, 8).toUpperCase()}
                      </TableCell>
                      <TableCell className="py-2 text-xs text-center tabular-nums">
                        {r.numero_parcela}
                        {r.total_parcelas ? `/${r.total_parcelas}` : ''}
                      </TableCell>
                      <TableCell className="py-2 text-xs whitespace-nowrap">
                        {fmtDate(r.data_vencimento)}
                      </TableCell>
                      <TableCell className="py-2 text-xs text-center tabular-nums">
                        {r.dias_vencido > 0 ? (
                          <span className="text-red-600 font-medium">+{r.dias_vencido}</span>
                        ) : r.dias_vencido === 0 ? (
                          <span className="text-amber-600 font-medium">Hoje</span>
                        ) : (
                          <span className="text-muted-foreground">{r.dias_vencido}</span>
                        )}
                      </TableCell>
                      <TableCell className="py-2">
                        <Badge
                          variant="outline"
                          className={cn('text-[10px] whitespace-nowrap', statusCls)}
                        >
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-2 text-sm text-right font-medium tabular-nums">
                        {fmtBRL(Number(r.valor))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Rodapé: soma da página + paginação */}
          <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground pt-1">
            <div>
              {total > 0 && (
                <>
                  Soma da página: <span className="font-semibold text-foreground">{fmtBRL(pageSum)}</span>
                </>
              )}
            </div>
            <div>Total: {total} parcela{total === 1 ? '' : 's'}</div>
          </div>

          {total > pageSize && (
            <PaginationFooter
              totalItems={total}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(sz) => {
                setPageSize(sz);
                setPage(1);
              }}
              pageSizeOptions={[15, 30, 50]}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Drill-down do tenant selecionado */}
      <WlTenantDetailsModal
        tenantId={selectedTenantId}
        open={!!selectedTenantId}
        onClose={() => setSelectedTenantId(null)}
      />
    </>
  );
}
